import { US_CURRENCY_FORMAT } from 'Constants/currencyConstants';

// Money formatting for the whole admin front-end. There is deliberately no locale -> currency
// table here: the backend hands us the location's Rails I18n currency format (unit/format/
// precision), the same data `number_to_currency` uses, so the two always agree and adding a
// currency needs no front-end change at all (TD1-10201, TD1-10207).
//
// Intl renders the digits, the backend's delimiter/separator supply the grouping and decimal
// characters, and its `format` places the symbol. We
// cannot ask Intl for the symbol itself: Intl.NumberFormat's `currency` style demands an ISO 4217
// code and throws RangeError on "$". We do not take Intl's separator characters either, because
// Intl and rails-i18n disagree for some locales -- fr-CA groups with U+00A0 in Intl and a plain
// space in Rails, which looks identical on screen and silently breaks exact-string assertions.

/** @typedef {import('../types/CurrencyFormat').default} CurrencyFormat */

// Grouping positions come from here and never from the location, because Rails groups every three
// digits in every locale and CLDR does not. See the comment at the Intl.NumberFormat call.
const GROUPING_LOCALE = 'en-US';

/** @param {CurrencyFormat} [currencyFormat] @returns {CurrencyFormat} */
export const currencyFormatOf = (currencyFormat) =>
  currencyFormat && currencyFormat.unit ? currencyFormat : US_CURRENCY_FORMAT;

// The bare display symbol, for labels and table headers ("$", "CAD").
/** @param {CurrencyFormat} [currencyFormat] @returns {string} */
export const currencyUnit = (currencyFormat) => currencyFormatOf(currencyFormat).unit;

// The symbol as a leading prefix, including the separating space the locale asks for
// ("$", "CAD "). For inputs and cells that render {prefix}{number} themselves.
// ponytail: prefix-shaped only. A suffix locale ("%n %u") still reads as a prefix in these
// widgets; give them the full formatter if we ever sell into one.
/** @param {CurrencyFormat} [currencyFormat] @returns {string} */
export const currencyPrefix = (currencyFormat) => {
  const fmt = currencyFormatOf(currencyFormat);
  return fmt.format === '%u%n' ? fmt.unit : `${fmt.unit} `;
};

// Reads a money value back out of a string that was already rendered -- the reconciliation check
// in RentalBilling scrapes it from innerHTML, where a U+00A0 delimiter arrives as the literal
// "&nbsp;" rather than the character. Currency-agnostic by construction: the grouping and decimal
// marks come from the location's own format, so a decimal-comma locale ("1.234,50 EUR") reads as
// 1234.5 instead of 1.23450. Anything else that is not a digit, sign or point is dropped.
/** @param {string} value @param {CurrencyFormat} [currencyFormat] @returns {number} */
export const parseCurrencyValue = (value, currencyFormat) => {
  if (!value) return 0;
  const { delimiter, separator } = currencyFormatOf(currencyFormat);
  const price = parseFloat(
    value
      .split(delimiter)
      .join('')
      .replace(separator, '.')
      .replace(/[^\d.-]/g, '')
  );
  return Number.isNaN(price) ? 0 : price;
};

/**
 * @param {{ locale?: string, currencyFormat?: CurrencyFormat } & Record<string, any>} [options]
 * @returns {{ format: (value: number | string) => string }}
 */
const CurrencyFormatter = ({ locale, currencyFormat, ...others } = {}) => {
  const fmt = currencyFormatOf(currencyFormat);
  // A caller may ask for more decimals than the currency has (minimumFractionDigits: 2 is
  // common), and some currencies have precision 0 -- ja/ko/hu in rails-i18n. Intl throws
  // RangeError when the minimum exceeds the maximum, so the currency's precision is the
  // ceiling and the request is clamped to it rather than fought.
  const digitOptions = {
    style: 'decimal',
    minimumFractionDigits: fmt.precision,
    maximumFractionDigits: fmt.precision,
    ...others,
  };
  digitOptions.minimumFractionDigits = Math.min(
    digitOptions.minimumFractionDigits,
    digitOptions.maximumFractionDigits
  );
  // Fixed grouping locale on purpose -- NOT the location's. Rails' number_to_currency groups
  // every three digits for every locale, while CLDR does not: es/pl/pt-PT carry
  // minimumGroupingDigits 2 (Intl gives "1234,50" where Rails gives "1.234,50") and en-IN groups
  // in lakhs ("12,34,567" vs "1,234,567"). Taking Intl's positions would break the one promise
  // this module makes. It also keeps a free-form locales.locale value out of Intl, which throws
  // RangeError on a malformed tag such as "en_US" and would blank every money value on the page.
  const digits = new Intl.NumberFormat(GROUPING_LOCALE, digitOptions);

  // Intl's grouping, our marks.
  const groupedDigits = (magnitude) =>
    digits
      .formatToParts(magnitude)
      .map((part) => {
        if (part.type === 'group') return fmt.delimiter;
        if (part.type === 'decimal') return fmt.separator;
        return part.value;
      })
      .join('');

  return {
    format: (value) => {
      const number = Number(value) || 0;
      // Rails puts the sign outside the whole thing ("-CAD 30.00", "-30,00 €"), so format the
      // magnitude and re-attach the sign. This splits the sign off, it never hides it -- a
      // negative still renders negative.
      const body = fmt.format
        .replace('%u', fmt.unit)
        .replace('%n', groupedDigits(Math.abs(number)));
      return number < 0 ? `-${body}` : body;
    },
  };
};

export default CurrencyFormatter;
